import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Heading, Spinner, Text, VStack } from '@chakra-ui/react';

export default function ServiceUnavailableOverlay() {
  const [unavailable, setUnavailable] = useState(false);
  const [checking, setChecking] = useState(false);
  // Only a recovery remounts the app. Announcing "restored" on every healthy
  // page load remounted the whole tree mid-load, which dropped the plan a
  // page was opening (and any ?view= it was asked for).
  const wasUnavailable = useRef(false);
  const markUnavailable = useCallback(() => { wasUnavailable.current = true; setUnavailable(true); }, []);

  const checkReadiness = useCallback(async () => {
    setChecking(true);
    try {
      const response = await fetch('/health/ready?force=1', { cache: 'no-store' });
      if (response.ok) {
        setUnavailable(false);
        if (wasUnavailable.current) {
          wasUnavailable.current = false;
          window.dispatchEvent(new CustomEvent('pabs:service-restored'));
        }
      } else {
        markUnavailable();
      }
    } catch {
      markUnavailable();
    } finally {
      setChecking(false);
    }
  }, [markUnavailable]);

  useEffect(() => {
    const show = () => markUnavailable();
    window.addEventListener('pabs:service-unavailable', show);
    checkReadiness();
    return () => window.removeEventListener('pabs:service-unavailable', show);
  }, [checkReadiness, markUnavailable]);

  useEffect(() => {
    if (!unavailable) return undefined;
    const interval = window.setInterval(checkReadiness, 15000);
    return () => window.clearInterval(interval);
  }, [unavailable, checkReadiness]);

  if (!unavailable) return null;

  return (
    <Box position="fixed" inset={0} zIndex={20000} bg="gray.50" display="flex" alignItems="center" justifyContent="center" p={6}>
      <VStack spacing={5} maxW="520px" textAlign="center" bg="white" border="1px solid" borderColor="gray.200" borderRadius="2xl" p={{ base: 7, md: 10 }} boxShadow="xl">
        <Box as="span" className="material-icons-outlined" fontSize="42px" color="orange.400">cloud_off</Box>
        <Heading size="lg" color="gray.900">Pabs is temporarily unavailable</Heading>
        <Text color="gray.600" lineHeight="1.7">
          We’re having trouble reaching the service that stores your Pabs data. Your work is safe, and this page will reconnect automatically.
        </Text>
        <Button colorScheme="blue" onClick={checkReadiness} isDisabled={checking} leftIcon={checking ? <Spinner size="xs" /> : undefined}>
          {checking ? 'Checking…' : 'Try again'}
        </Button>
        <Text fontSize="xs" color="gray.400">You don’t need to keep navigating or refresh repeatedly.</Text>
      </VStack>
    </Box>
  );
}
